import { useEffect, useState } from "react";
import { api, resolveImage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import { Field, MultilangField } from "@/pages/admin/AdminOffers";

const empty = {
    slug: "",
    name: { de: "", tr: "", en: "" },
    description: { de: "", tr: "", en: "" },
    image_url: "",
    order: 0,
    active: true,
};

export default function AdminCategories() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = () => api.get("/admin/categories").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
    const startEdit = (it) => {
        setEditing(it);
        setForm({
            slug: it.slug,
            name: it.name || { de: "", tr: "", en: "" },
            description: it.description || { de: "", tr: "", en: "" },
            image_url: it.image_url || "",
            order: it.order || 0,
            active: it.active !== false,
        });
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing) {
                await api.put(`/admin/categories/${editing.id}`, form);
                toast.success("Aktualisiert");
            } else {
                await api.post("/admin/categories", form);
                toast.success("Angelegt");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Fehler beim Speichern");
        }
    };

    const del = async (it) => {
        if (!confirm(`„${it.name?.de}" wirklich löschen?`)) return;
        await api.delete(`/admin/categories/${it.id}`);
        toast.success("Gelöscht");
        load();
    };

    return (
        <div className="p-6 md:p-10 max-w-7xl">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-brand-green">Kategorien</div>
                    <h1 className="mt-1 font-display text-3xl font-bold">Produktkategorien</h1>
                </div>
                <button onClick={startCreate} className="btn-primary" data-testid="category-create"><Plus className="h-4 w-4" />Neue Kategorie</button>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((it) => (
                    <div key={it.id} className="card-clean overflow-hidden" data-testid={`category-row-${it.slug}`}>
                        <div className="aspect-[4/3] bg-brand-cream-dark relative overflow-hidden">
                            {it.image_url && <img src={resolveImage(it.image_url)} alt="" className="absolute inset-0 h-full w-full object-cover" />}
                        </div>
                        <div className="p-4">
                            <div className="font-display font-semibold">{it.name?.de}</div>
                            <div className="text-xs text-brand-charcoal/60 mt-1">{it.slug}</div>
                            <div className="mt-3 flex justify-end gap-1">
                                <button onClick={() => startEdit(it)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-brand-cream-dark"><Pencil className="h-4 w-4" /></button>
                                <button onClick={() => del(it)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-brand-cream-dark text-brand-red"><Trash2 className="h-4 w-4" /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{editing ? "Kategorie bearbeiten" : "Neue Kategorie"}</DialogTitle></DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <Field label="Slug"><input required value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") }))} className="input-clean" data-testid="category-slug" /></Field>
                        <MultilangField label="Name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
                        <MultilangField label="Beschreibung" value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} multiline />
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-widest text-brand-charcoal/60 mb-2">Bild</div>
                            <ImageUploader value={form.image_url} onChange={(v) => setForm((f) => ({ ...f, image_url: v }))} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Reihenfolge"><input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value || "0") }))} className="input-clean" /></Field>
                            <Field label="Status"><label className="inline-flex items-center gap-2 mt-3"><input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} /> Aktiv</label></Field>
                        </div>
                        <DialogFooter className="pt-2">
                            <button type="button" className="btn-outline-dark" onClick={() => setOpen(false)}>Abbrechen</button>
                            <button type="submit" className="btn-primary" data-testid="category-save">Speichern</button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
